import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TemplateService } from '../../services/template.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ProjecttemplateService } from '../../services/projecttemplate.service';
import { ProjectTemplateModel, ProjectTemplateSkidAssignmentModel } from '../../models/project.model';
import { TemplateTypeModel, BusinessUnitModel } from '../../models/template.model';
import { BlockScheduleModel } from '../../models/blockschedule.model';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';
import { TEMPLATE_TYPE } from '../../models/template.constants';
import { ProjectStateService } from '../../services/project-state.service';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';
import { ProjectService } from '../../services/project.service';
import { Subject, forkJoin, of, takeUntil } from 'rxjs';
import { catchError, map, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-project-template',
  imports: [ToastModule,
    CommonModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    RouterLink,
    DialogModule,
    CheckboxModule,
    ToggleSwitch,
    TooltipModule,
    FormsModule,
    ReactiveFormsModule],
  templateUrl: './project-template.html',
  styleUrls: ['./project-template.css'],
  providers: [MessageService]
})
export class ProjectTemplateComponent implements OnInit, OnDestroy {
  // Expose constants to template
  readonly TEMPLATE_TYPE = TEMPLATE_TYPE;

  activeTab: string = '0';
  projectId: number = 0;
    isEdit: boolean = false;
    showDialog: boolean = false;
  dialogMaximized: boolean = false;
  dialogOpening: boolean = false;
  showCloneDialog: boolean = false;
  cloneTemplateName = '';
  cloneSourceTemplateId: number | null = null;
  cloneTemplateType: number = TEMPLATE_TYPE.TEMPLATE;
  currentTemplateType: number = TEMPLATE_TYPE.TEMPLATE;
  form!: FormGroup;
  templates: ProjectTemplateModel[] = [];
  forms: ProjectTemplateModel[] = [];
  genericTemplates: ProjectTemplateModel[] = [];
  filteredTemplates: ProjectTemplateModel[] = [];
  filteredForms: ProjectTemplateModel[] = [];
  filteredGenericTemplates: ProjectTemplateModel[] = [];
  templateTypeList: TemplateTypeModel[] = [];
  projectRoles: any[] = [];
  businessUnits: BusinessUnitModel[] = [];
  isReadOnly: boolean = false;
  canView: boolean = false;
  canCreate: boolean = false;
  canUpdate: boolean = false;
  canDelete: boolean = false;
  canEquipmentAssign: boolean = false;
  isProjectInactive: boolean = false;
  expandedGroupsTemplates: { [key: string]: boolean } = {};
  expandedGroupsForms: { [key: string]: boolean } = {};
  expandedGroupsGeneric: { [key: string]: boolean } = {};
  skidOptions: { label: string; value: number }[] = [];
  templateSkidSelectionMap: { [templateId: number]: number[] } = {};
  templateSkidSavingMap: { [templateId: number]: boolean } = {};
  private templateSkidAssignmentsByTemplate: { [templateId: number]: number[] } = {};
  private templateSkidLocksByTemplate: { [templateId: number]: Set<number> } = {};
  private dataLoaded = false;
  private assignmentContextLoaded = false;
  private destroy$ = new Subject<void>();
  constructor(private projecttemplateService: ProjecttemplateService,
    private templateService: TemplateService,
    private projectService: ProjectService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private projectStateService: ProjectStateService,
    private permissionHelper: PermissionHelperService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {

    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const projectIdParam = params.get('projectId');
        if (projectIdParam) {
          this.projectId = parseInt(projectIdParam);
          this.dataLoaded = false;
          this.assignmentContextLoaded = false;
          this.templates = [];
          this.forms = [];
          this.genericTemplates = [];
          this.filteredTemplates = [];
          this.filteredForms = [];
          this.filteredGenericTemplates = [];
          this.templateSkidSelectionMap = {};
          this.templateSkidAssignmentsByTemplate = {};
          this.templateSkidLocksByTemplate = {};
          this.skidOptions = [];
          console.log('Project ID from URL:', this.projectId);

          if (this.projectId > 0) {
            this.tryLoadTemplates();
          } else {
            console.error('Invalid project ID:', this.projectId);
          }
        } else {
          console.error('No project ID found in URL parameters');
        }
      });
  }
  ngOnInit(): void {
    this.initForm({} as ProjectTemplateModel);
    this.loadTemplateTypes();
    this.loadProjectRoles();
    this.loadBusinessUnits();
    this.projectStateService.pageSearch$.pipe(takeUntil(this.destroy$)).subscribe(search => {
      this.applySearch(search);
    });

    this.projectStateService.currentProjectUserActive$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isActive => {
        this.isProjectInactive = isActive === false;
        this.updateReadOnlyState();
      });

    this.projectStateService.currentProjectPermissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.canView = this.permissionHelper.has(PAGE_KEYS.TEMPLATES, PERMISSION_KEYS.VIEW);
        this.canCreate = this.permissionHelper.has(PAGE_KEYS.TEMPLATES, PERMISSION_KEYS.CREATE);
        this.canUpdate = this.permissionHelper.has(PAGE_KEYS.TEMPLATES, PERMISSION_KEYS.UPDATE);
        this.canDelete = this.permissionHelper.has(PAGE_KEYS.TEMPLATES, PERMISSION_KEYS.DELETE);
        this.canEquipmentAssign = this.permissionHelper.has(PAGE_KEYS.EQUIPMENT, PERMISSION_KEYS.ASSIGN);
        this.updateReadOnlyState();
        this.tryLoadTemplates();
      });
  }

  loadProjectRoles(): void {
    this.projectService.getRoleList().subscribe({
      next: (roles) => {
        console.log('Fetched project roles:', roles);
        this.projectRoles = roles;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error fetching project roles:', error);
      }
    });
  }

  loadBusinessUnits(): void {
    this.templateService.getBusinessUnits().subscribe({
      next: (businessUnits) => {
        console.log('Fetched business units:', businessUnits);
        this.businessUnits = (businessUnits || []).map(x => ({
          ...x,
          displayName: x.displayName || x.name
        }));
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error fetching business units:', error);
      }
    });
  }

  private applySearch(search: string): void {
    const term = (search || '').trim().toLowerCase();
    if (!term) {
      this.filteredTemplates = [...this.templates];
      this.filteredForms = [...this.forms];
      this.filteredGenericTemplates = [...this.genericTemplates];
    } else {
      this.filteredTemplates = this.templates.filter(t => (t.templateName || '').toLowerCase().includes(term));
      this.filteredForms = this.forms.filter(t => (t.templateName || '').toLowerCase().includes(term));
      this.filteredGenericTemplates = this.genericTemplates.filter(t => (t.templateName || '').toLowerCase().includes(term));
    }
    this.cdr.markForCheck();
  }

  private updateReadOnlyState(): void {
    this.isReadOnly = this.isProjectInactive || (!this.canCreate && !this.canUpdate && !this.canDelete);
  }

  private tryLoadTemplates(): void {
    if (!this.projectId || this.projectId <= 0) return;
    if (!this.canView) return;
    if (!this.assignmentContextLoaded) {
      this.loadSkidOptions();
      this.loadTemplateSkidAssignments();
      this.assignmentContextLoaded = true;
    }
    if (this.dataLoaded) return;
    this.loadTemplates(TEMPLATE_TYPE.TEMPLATE);
    this.loadTemplates(TEMPLATE_TYPE.FORMS);
    this.loadTemplates(TEMPLATE_TYPE.GENERIC);
    this.dataLoaded = true;
  }

  get canSaveTemplate(): boolean {
    const allowed = this.isEdit ? this.canUpdate : this.canCreate;
    return allowed && !this.isProjectInactive;
  }

  initForm(item: ProjectTemplateModel) {
    this.form = this.fb.group({
      templateId: [item.templateId || 0],
      projectId: [item.projectId || this.projectId],
      templateName: [item.templateName || '', Validators.required],
      shortCode: [item.shortCode || '', [Validators.required, Validators.maxLength(16)]],
      description: [item.description || ''],
      typeId: [item.typeId || null, Validators.required],
      businessUnitId: [item.businessUnitId || null],
      isActive: [item.isActive || false],
      selectedRoleIds: [item.selectedRoleIds || []]
    });
    // Enable typeId by default (will be disabled in openEditTemplate for edit mode)
    this.form.get('typeId')?.enable();
  }

  loadTemplateTypes(): void {
    this.templateService.getTemplateTypeList().subscribe({
      next: (types) => {
        console.log('Fetched template types:', types);
        this.templateTypeList = types;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error fetching template types:', error);
      }
    });
  }

  loadTemplates(templateTypeId: number): void {
    if (this.projectId > 0) {
      this.projecttemplateService.getProjectTemplatesByType(this.projectId, templateTypeId).subscribe({
        next: (templates) => {
          console.log(`Fetched templates for type ${templateTypeId}:`, templates);
          if (templateTypeId === TEMPLATE_TYPE.TEMPLATE) {
            this.templates = templates;
            this.syncTemplateSkidSelections();
          } else if (templateTypeId === TEMPLATE_TYPE.FORMS) {
            this.forms = templates;
          } else if (templateTypeId === TEMPLATE_TYPE.GENERIC) {
            this.genericTemplates = templates;
          }
          this.applySearch(this.projectStateService.getPageSearch());
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error(`Error fetching templates for type ${templateTypeId}:`, error);
        }
      });
    }
  }

  private loadSkidOptions(): void {
    this.projectService.getProjectBlockSchedule(this.projectId).subscribe({
      next: (schedules: BlockScheduleModel[]) => {
        this.skidOptions = (schedules || [])
          .filter(s => typeof s.blockScheduleId === 'number')
          .map(s => ({
            label: this.getSkidOptionLabel(s),
            value: s.blockScheduleId
          }));
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading skid options:', error);
        this.skidOptions = [];
      }
    });
  }

  private getSkidOptionLabel(skid: BlockScheduleModel): string {
    const base = (skid.name || skid.blockName || '').trim()
      || (typeof skid.tmeicSkidNo === 'number' ? `MVPS #${skid.tmeicSkidNo}` : `MVPS #${skid.blockScheduleId}`);
    const config = this.parseSkidConfigLabel(skid.skidConfiguration);
    return config ? `${base} | ${config}` : base;
  }

  private parseSkidConfigLabel(skidConfiguration?: string | null): string {
    if (!skidConfiguration) return '';
    const raw = skidConfiguration.trim();
    // Already friendly format e.g. "N4" or "N4,N6"
    if (!/^\d/.test(raw)) {
      // Handle comma-separated configs like "N4,N6"
      return raw.split(',').map(s => s.trim()).filter(Boolean).join(' | ');
    }
    // Raw numeric format e.g. "0-0-0-1-2-0-0"
    return raw.split('-')
      .map((val, i) => ({ n: i + 1, count: parseInt(val, 10) }))
      .filter(x => x.count > 0)
      .map(x => `N${x.n}`)
      .join(' | ');
  }

  private loadTemplateSkidAssignments(): void {
    this.projecttemplateService.getSkidTemplateAssignmentsByUserRole(this.projectId, TEMPLATE_TYPE.TEMPLATE).subscribe({
      next: (rows: ProjectTemplateSkidAssignmentModel[]) => {
        const grouped: { [templateId: number]: number[] } = {};
        const lockedByTemplate: { [templateId: number]: Set<number> } = {};
        for (const row of rows || []) {
          if (!grouped[row.projectTemplateId]) grouped[row.projectTemplateId] = [];
          grouped[row.projectTemplateId].push(row.blockScheduleId);
          if (row.hasChecklistResponses) {
            if (!lockedByTemplate[row.projectTemplateId]) {
              lockedByTemplate[row.projectTemplateId] = new Set<number>();
            }
            lockedByTemplate[row.projectTemplateId].add(row.blockScheduleId);
          }
        }
        this.templateSkidAssignmentsByTemplate = grouped;
        this.templateSkidLocksByTemplate = lockedByTemplate;
        this.syncTemplateSkidSelections();
      },
      error: (error) => {
        console.error('Error loading template-skid assignments:', error);
        this.templateSkidAssignmentsByTemplate = {};
        this.templateSkidLocksByTemplate = {};
        this.syncTemplateSkidSelections();
      }
    });
  }

  private syncTemplateSkidSelections(): void {
    const updatedMap: { [templateId: number]: number[] } = {};
    for (const t of this.templates) {
      if (!t.templateId) continue;
      updatedMap[t.templateId] = [...(this.templateSkidAssignmentsByTemplate[t.templateId] || [])];
    }
    this.templateSkidSelectionMap = updatedMap;
    this.cdr.markForCheck();
  }

  getTemplateSkids(templateId?: number): number[] {
    if (!templateId) return [];
    return this.templateSkidSelectionMap[templateId] || [];
  }

  getTemplateSkidOptions(templateId?: number): Array<{ label: string; value: number; disabled?: boolean }> {
    if (!templateId) {
      return this.skidOptions;
    }

    const lockedSkids = this.templateSkidLocksByTemplate[templateId] || new Set<number>();
    return this.skidOptions.map((option) => ({
      ...option,
      disabled: lockedSkids.has(option.value)
    }));
  }

  isTemplateSkidSaving(templateId?: number): boolean {
    if (!templateId) return false;
    return !!this.templateSkidSavingMap[templateId];
  }

  onTemplateSkidsChange(template: ProjectTemplateModel, selectedSkidIds: number[]): void {
    if (!template.templateId || this.isProjectInactive || !this.canEquipmentAssign) {
      return;
    }

    const templateId = template.templateId;
    const next = Array.from(new Set((selectedSkidIds || []).map(Number).filter(Number.isFinite)));
    const prev = this.templateSkidSelectionMap[templateId] || [];

    const prevSet = new Set(prev);
    const nextSet = new Set(next);
    const toAdd = next.filter(id => !prevSet.has(id));
    const toRemove = prev.filter(id => !nextSet.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      return;
    }

    this.templateSkidSavingMap[templateId] = true;

    const operations: any[] = [];

    if (toAdd.length > 0) {
      operations.push(
        this.projecttemplateService.assignTemplatesToSkids({
          projectId: this.projectId,
          templateIds: [templateId],
          blockScheduleIds: toAdd
        }).pipe(
          map(() => ({ kind: 'add', ok: true as const })),
          catchError((error) => of({ kind: 'add', ok: false as const, error }))
        )
      );
    }

    for (const skidId of toRemove) {
      operations.push(
        this.projecttemplateService.unassignTemplateFromSkid(this.projectId, skidId, templateId).pipe(
          map(() => ({ kind: 'remove', skidId, ok: true as const })),
          catchError((error) => of({ kind: 'remove', skidId, ok: false as const, error }))
        )
      );
    }

    forkJoin(operations)
      .pipe(finalize(() => {
        this.templateSkidSavingMap[templateId] = false;
      }))
      .subscribe((results: any[]) => {
        const finalSelection = new Set(next);
        let hasError = false;

        for (const r of results) {
          if (r.ok) continue;

          hasError = true;
          const message = r?.error?.error?.message || 'Failed to update MV Power Station assignments.';

          if (r.kind === 'remove') {
            finalSelection.add(r.skidId);
          }

          if (r.kind === 'add') {
            for (const skidId of toAdd) finalSelection.delete(skidId);
          }

          this.messageService.add({
            severity: r?.error?.status === 409 ? 'warn' : 'error',
            summary: r?.error?.status === 409 ? 'Cannot Unassign' : 'Error',
            detail: message
          });
        }

        this.templateSkidSelectionMap[templateId] = Array.from(finalSelection);
        this.templateSkidAssignmentsByTemplate[templateId] = [...this.templateSkidSelectionMap[templateId]];

        if (!hasError) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'MV Power Station assignment updated.'
          });
        }

        this.cdr.markForCheck();
      });
  }

  deleteTemplate(template: ProjectTemplateModel, templateType: number = TEMPLATE_TYPE.TEMPLATE): void {
    if (!this.canDelete || this.isProjectInactive) {
      return;
    }
    if (template.templateId && confirm('Delete this template?')) {
      this.projecttemplateService.deleteProjectTemplate(template.templateId).subscribe({
        next: () => {
          const typeId = template.typeId || templateType;
          this.loadTemplates(typeId);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Template deleted successfully' });
        },
        error: (error) => {
          console.error('Error deleting template:', error);
          const detail = error?.error?.message || error?.message || 'Failed to delete template. Please try again.';
          this.messageService.add({ severity: 'error', summary: 'Error', detail });
        }
      });
    }
  }

  saveTemplate(): void {
    if (!this.canSaveTemplate) {
      return;
    }
    if (this.form.invalid) return;
    const template = this.form.getRawValue() as ProjectTemplateModel;
    console.log('Saving template:', template);
    if (this.isEdit && template.templateId > 0) {
      this.projecttemplateService.updateProjectTemplate(template).subscribe(() => {
        this.loadTemplates(this.currentTemplateType);
        // Defer dialog close to next change detection cycle
        setTimeout(() => {
          this.showDialog = false;
        }, 0);
      });
    } else {
      this.projecttemplateService.addProjectTemplate(template).subscribe(() => {
        this.loadTemplates(this.currentTemplateType);
        // Defer dialog close to next change detection cycle
        setTimeout(() => {
          this.showDialog = false;
        }, 0);
      });
    }
  }

  openEditTemplate(template: ProjectTemplateModel, templateType: number = TEMPLATE_TYPE.TEMPLATE): void {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
        this.isEdit = true;
        this.currentTemplateType = templateType;
        this.dialogMaximized = false;
        this.dialogOpening = true;

    // Load assigned roles for this template
    if (template.templateId) {
      this.projecttemplateService.getProjectTemplateRoles(template.templateId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (roleIds) => {
            console.log('Fetched template roles:', roleIds);
            template.selectedRoleIds = roleIds;
            this.initForm(template);
            this.form.get('typeId')?.disable();
            this.form.get('businessUnitId')?.disable();
            this.showDialog = true;
          },
          error: (error) => {
            console.error('Error fetching template roles:', error);
            // Still open dialog even if role fetch fails
            this.initForm(template);
            this.form.get('typeId')?.disable();
            this.form.get('businessUnitId')?.disable();
            this.showDialog = true;
          }
        });
    } else {
      this.initForm(template);
      this.form.get('typeId')?.disable();
      this.form.get('businessUnitId')?.disable();
      this.showDialog = true;
    }
  }

  openAddTemplate(templateType: number = TEMPLATE_TYPE.TEMPLATE): void {
    if (!this.canCreate || this.isProjectInactive) {
      return;
    }
        this.isEdit = false;
        this.currentTemplateType = templateType;
        this.dialogMaximized = false;
        this.dialogOpening = true;
    const newTemplate = {
      projectId: this.projectId,
      typeId: templateType
    } as ProjectTemplateModel;
      this.initForm(newTemplate);
      this.showDialog = true;
    }

  onTemplateDialogShow(dialog: { maximize: () => void }): void {
    dialog.maximize();
  }

  onTemplateDialogMaximize(event: { maximized: boolean }): void {
    this.dialogMaximized = !!event?.maximized;
    this.dialogOpening = false;
  }

  toggleProjectTemplateActive(template: ProjectTemplateModel): void {
    if (!this.canUpdate || this.isProjectInactive) {
      return;
    }
    const previous = !!template.isActive;
    const newValue = !!template.isActive;
    this.projecttemplateService.updateProjectTemplateActive(template.templateId, newValue).subscribe({
      error: (error) => {
        console.error('Error updating project template active status:', error);
        template.isActive = previous;
      }
    });
  }

  openCloneDialog(template: ProjectTemplateModel, templateType: number = TEMPLATE_TYPE.TEMPLATE): void {
    if (!this.canCreate || this.isProjectInactive) {
      return;
    }
    this.cloneSourceTemplateId = template.templateId || null;
    this.cloneTemplateType = templateType;
    this.cloneTemplateName = `${template.templateName || 'Template'}-copy`;
    this.showCloneDialog = true;
  }

  saveClone(): void {
    if (!this.canCreate || this.isProjectInactive) {
      return;
    }
    if (!this.cloneSourceTemplateId) {
      return;
    }
    const name = this.cloneTemplateName.trim();
    if (!name) {
      return;
    }

    this.projecttemplateService.cloneProjectTemplate({
      sourceProjectTemplateId: this.cloneSourceTemplateId,
      templateName: name
    }).subscribe({
      next: (newId) => {
        if (newId && newId > 0) {
          if (this.cloneTemplateType === TEMPLATE_TYPE.TEMPLATE) {
            this.loadTemplates(TEMPLATE_TYPE.TEMPLATE);
          } else if (this.cloneTemplateType === TEMPLATE_TYPE.FORMS) {
            this.loadTemplates(TEMPLATE_TYPE.FORMS);
          } else if (this.cloneTemplateType === TEMPLATE_TYPE.GENERIC) {
            this.loadTemplates(TEMPLATE_TYPE.GENERIC);
          }
        }
        this.showCloneDialog = false;
        this.cloneTemplateName = '';
        this.cloneSourceTemplateId = null;
      },
      error: (error) => {
        console.error('Error cloning template:', error);
      }
    });
  }

  get activeTabType(): number {
    if (this.activeTab === '1') return TEMPLATE_TYPE.FORMS;
    if (this.activeTab === '2') return TEMPLATE_TYPE.GENERIC;
    return TEMPLATE_TYPE.TEMPLATE;
  }

  navigateToChecksheet(templateId: number): void {
    this.router.navigate(['/projectchecksheet', this.projectId, templateId]);
  }

  getGroupedData(templates: ProjectTemplateModel[]): { businessUnitId: number | null; name: string; items: ProjectTemplateModel[] }[] {
    const groups: { [key: string]: { businessUnitId: number | null; name: string; items: ProjectTemplateModel[] } } = {};
    for (const t of templates) {
      const key = String(t.businessUnitId ?? 'null');
      if (!groups[key]) {
        groups[key] = {
          businessUnitId: t.businessUnitId ?? null,
          name: this.getBusinessUnitName(t.businessUnitId),
          items: []
        };
      }
      groups[key].items.push(t);
    }
    return Object.values(groups)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(g => ({ ...g, items: g.items.sort((a, b) => (a.templateName || '').localeCompare(b.templateName || '')) }));
  }

  getTemplateTypeName(typeId: number | null | undefined): string {
    const type = this.templateTypeList.find(t => t.templateTypeId === typeId);
    return type ? type.typeName : 'N/A';
  }

  getBusinessUnitName(businessUnitId: number | null | undefined): string {
    if (!businessUnitId) return 'N/A';
    const unit = this.businessUnits.find(b => b.id === businessUnitId);
    return unit ? (unit.displayName || unit.name) : 'N/A';
  }

  isGroupExpanded(groupType: 'templates' | 'forms' | 'generic', businessUnitId: number | null | undefined): boolean {
    const key = String(businessUnitId ?? 'N/A');
    const target = this.getExpandedGroupRef(groupType);
    return !!target[key];
  }

  toggleGroup(groupType: 'templates' | 'forms' | 'generic', businessUnitId: number | null | undefined): void {
    const key = String(businessUnitId ?? 'N/A');
    const target = this.getExpandedGroupRef(groupType);
    target[key] = !target[key];
  }

  private getExpandedGroupRef(groupType: 'templates' | 'forms' | 'generic') {
    if (groupType === 'forms') return this.expandedGroupsForms;
    if (groupType === 'generic') return this.expandedGroupsGeneric;
    return this.expandedGroupsTemplates;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
